import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { Box, CircularProgress, Typography, Alert, Paper } from '@mui/material';

// Supports either requiredRole="vendor" or allowedRoles={["vendor","panelist"]}
const ProtectedRoute = ({ children, requiredRole = null, allowedRoles = null }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          bgcolor: '#f5f5f5'
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2, color: '#5c6ac4' }} />
        <Typography variant="h6" color="text.secondary">
          Checking authentication...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we verify your credentials
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access for either a single role or a list
  const rolesOk = (
    !requiredRole && !allowedRoles
  ) || (
    requiredRole && user?.role === requiredRole
  ) || (
    Array.isArray(allowedRoles) && allowedRoles.includes(user?.role)
  );

  if (!rolesOk) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          p: 3
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
              🚫 Access Denied
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You need <strong>{requiredRole}</strong> permissions to access this page.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your current role: <strong>{user?.role || 'Unknown'}</strong>
            </Typography>
          </Alert>
          
          <Typography variant="body2" color="text.secondary">
            Contact your administrator if you believe this is an error, or return to your appropriate dashboard.
          </Typography>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }} 
              onClick={() => window.history.back()}>
              ← Go Back
            </Typography>
            <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}
              onClick={() => window.location.href = '/login'}>
              Login as Different User →
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Render the protected component
  return children;
};

export default ProtectedRoute;
